import {
  AttendeeFragment,
  PublicEWebinarFragment,
  PublicSetFragment,
  RegisterAttendeeToWebinarInput,
  RegisterType,
  RoomType,
  Session,
  useRegisterAttendeeToWebinarMutation,
} from '$/GraphQL/schema/schema';
import { InteractionInfo } from '$/components/Interactions/InteractionInfo';
import { Portal } from '$/components/Portal';
import SessionsDropdown from '$/components/ScheduleDropdownInput';
import { Truncate } from '$/components/Truncate/Truncate';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { FilledMarkdown } from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { Mentionable } from '$/components/ui/EditableInput/types';
import Form, { FormOptionsContext } from '$/components/ui/Form';
import PageBuilder from '$/components/ui/Input/PageBuilder';
import { PageBuilderState, PageMode } from '$/components/ui/Input/PageBuilder.types';
import { ErrorMessage } from '$/components/ui/MessageText';
import { useEwebinarSession } from '$/hooks/useEwebinarSession';
import { useServerContext } from '$/hooks/useServerDateNow';
import { EDate } from '$/shared/frontend/utils/EDate';
import { isLangRtl, langDirClassname } from '$/shared/frontend/utils/i18n';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { getLocale } from '$/utils/getLocale';
import isMobile from '$/utils/isMobile';
import { logger } from '$/utils/logger';
import { useFBPixelContext } from '$/utils/useFBPixel';
import { css } from '@emotion/react';
import { ApolloError } from 'apollo-client';
import { AnimatePresence, motion } from 'framer-motion';
import React, { Suspense, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import CardMain from '../cards/CardMain';
import { NextWebinarModalTitle } from './NextWebinarCardTitle';
import Input from '$/components/ui/Input/Basics/Input';

const RegistrationForm = React.lazy(
  () => import('$/sections/PublishedWebinar/Registration/RegistrationForm')
);

const RegisterNowButton: React.FC<{
  interaction: InteractionInfo;
  state: PageBuilderState;
  disabled?: boolean;
  onRegister: () => Promise<void>;
}> = ({ interaction, state, disabled, onRegister }) => {
  const { details } = interaction;

  if (state.mode === PageMode.edit) {
    return (
      <div className='flex justify-end'>
        <PageBuilder.CardButton
          state={state}
          name='details.buttonText'
          id='btnRegister'
          className='rounded-full'
          placeholder={'Enter button call to action'}
          defaultValue={details?.buttonText ?? 'Register now'}
        />
      </div>
    );
  }

  return (
    <CardMain.SubmitButton
      disabled={disabled}
      onClick={async (e): Promise<void> => {
        e.preventDefault();
        onRegister();
      }}
    >
      {details!.buttonText!}
    </CardMain.SubmitButton>
  );
};

export const NextWebinarItem: React.FC<{
  set: PublicSetFragment;
  nextWebinar: PublicEWebinarFragment;
  interaction: InteractionInfo;
  state: PageBuilderState;
  attendee?: AttendeeFragment;
  mentions?: Mentionable[];
}> = withWebinarPlayerContext(
  ['rooms', 'ewebinar', 'animations', 'setFocusedCardMode'],
  'NextWebinarItem'
)(({ set, nextWebinar, interaction, state, attendee, mentions, context }) => {
  const [openRegForm, setOpenRegForm] = React.useState(false);
  const [formError, setFormError] = React.useState<string>();
  const { pixelID, FBPixel } = useFBPixelContext();
  const [registerAttendeeToWebinar] = useRegisterAttendeeToWebinarMutation();

  const { onSubmit } = useContext(FormOptionsContext);
  const { setValue, watch } = useFormContext();
  const { rooms, ewebinar, animations, setFocusedCardMode } = context;
  const { serverDateNow } = useServerContext();

  const { details } = interaction;

  // Next minute after end of this presentation room (or exit room if past presentation already) + 5 sec buffer
  const startTime = attendee?.startTime ?? ewebinar.startDate!;
  let endsAt = new EDate(startTime, 'UTC').addMinutes(
    Math.ceil((rooms.getRoom(RoomType.Presentation).endsAt + 5) / 60)
  );
  if (new EDate(serverDateNow(), 'UTC').diffInMinutes(endsAt) > 0) {
    // End of video is in the past - we are in the exit room.  Calc end of the exit room then.
    endsAt = new EDate(startTime, 'UTC').addMinutes(
      Math.ceil((rooms.getRoom(RoomType.Exit).endsAt + 5) / 60)
    );
  }

  // Make sure and use endOfWebinarSessionTime to power useMemo here as that is a static value
  // that doesn't change w/ playbackPosition and is not an object like EDate
  const endsAtTime = endsAt.getTime();
  const endOfWebinarSessionDate = React.useMemo(() => {
    return nextWebinar.justInTimeModeEnabled
      ? new EDate(endsAtTime, LocalTimeZone.name)
      : undefined;
  }, [endsAtTime, nextWebinar.justInTimeModeEnabled]);

  const endOfWebinarName = 'At the end of this webinar';

  // useMemo here is critical.  We don't want children re-rendering because we're passing in
  // a property bag that changes every render.  It only needs to change w/ the value of endOfWebinarSessionIn
  const { sessions: ewebinarSession } = useEwebinarSession({
    ewebinar: nextWebinar,
    timezone: LocalTimeZone.name,
    skipFromNow: false,
    justInTimeDate: endOfWebinarSessionDate,
    justInTimeName: endOfWebinarName,
    serverDateNow,
  });

  const onClose = React.useCallback(() => {
    setOpenRegForm((open) => {
      if (open) {
        logger.debug('ANIM', 'start ', ['actions.overlay.close']);
        isMobile && setFocusedCardMode(false);
        animations.start('actions.overlay.close');
      }
      return false;
    });
  }, [animations, setFocusedCardMode]);

  const onSubmitReaction = React.useCallback(
    (attendeeId: string, setId: string, sessionType?: string | null): void => {
      setValue('details.attendeeId', attendeeId, { shouldDirty: true });
      setValue('details.setId', setId, { shouldDirty: true });
      setValue('details.sessionType', sessionType, { shouldDirty: true });
      setValue('details.ewebinarSession', undefined, { shouldDirty: true });

      setTimeout(() => {
        onSubmit();
        onClose();
      }, 0);
    },
    [onClose, onSubmit, setValue]
  );

  const autoRegisterWebinar = React.useCallback(async (): Promise<void> => {
    const session = watch('details.ewebinarSession');
    if (!attendee || !session || interaction.details?.registerType !== RegisterType.AutoRegister) {
      return;
    }

    const attendeeInput: RegisterAttendeeToWebinarInput = {
      attendeeId: attendee.id,
      setId: set.id,
      startTime: session.value,
      sessionType: session.type,
      timezone: LocalTimeZone.name,
      locale: getLocale(),
    };

    try {
      await registerAttendeeToWebinar({
        variables: { data: { ...attendeeInput } },
        update: async (_cache, mutationResult) => {
          if (mutationResult.data && mutationResult.data.registerAttendeeToWebinar) {
            const attendee = mutationResult.data.registerAttendeeToWebinar;
            await FBPixel.trackCompleteRegistration(pixelID, attendee.email);
            // Submit reaction
            onSubmitReaction(attendee.id, set.id, attendee?.sessionType);
          }
        },
      });
    } catch (e) {
      const error = e as ApolloError;
      setFormError(
        error.graphQLErrors && error.graphQLErrors.length > 0
          ? error.graphQLErrors[0].message
          : error.message
      );
    }
  }, [
    FBPixel,
    attendee,
    interaction.details?.registerType,
    onSubmitReaction,
    pixelID,
    registerAttendeeToWebinar,
    set.id,
    watch,
  ]);

  const onRegister = React.useCallback(async (): Promise<void> => {
    if (details?.registerType === RegisterType.AutoRegister) {
      await autoRegisterWebinar();
    } else if (details?.registerType === RegisterType.PopupRegistrationForm) {
      isMobile && setFocusedCardMode(true);
      animations.start('actions.overlay.open');
      setOpenRegForm(true);
    }
  }, [animations, autoRegisterWebinar, details?.registerType, setFocusedCardMode]);

  React.useEffect(() => {
    // Auto select if there is only one session
    if (state.mode !== PageMode.public) return;
    if (ewebinarSession?.sessions.length === 1) {
      setValue('details.ewebinarSession', ewebinarSession.sessions[0], { shouldDirty: true });
    }
  }, [ewebinarSession?.sessions, state.mode, setValue]);

  // hide if there is no session
  if (
    state.mode === PageMode.public &&
    (!ewebinarSession?.sessions || ewebinarSession?.sessions.length === 0)
  ) {
    return null;
  }

  const lang = nextWebinar.viewerRoomSettings.language ?? 'en';

  return (
    <div className={`flex flex-col p-0 ${langDirClassname(lang)}`}>
      {state.mode === PageMode.edit && (
        <NextWebinarModalTitle
          interaction={interaction}
          imageMediaRef={set.socialMediaTags.mediaRef}
        />
      )}

      {state.mode === PageMode.public && (
        <>
          <Form.Field
            name='details.attendeeId'
            component={<Input type='hidden' />}
            className='hidden'
          />
          <Form.Field
            name='details.sessionType'
            component={<Input type='hidden' />}
            className='hidden'
          />
          <Form.Field name='details.setId' component={<Input type='hidden' />} className='hidden' />
        </>
      )}
      <div
        className={`
          w-full flex flex-col
          ${state.mode === PageMode.edit && 'p-1e'}
          pt-0 rounded-b-2xl
        `}
      >
        <div className='flex justify-between flex-col md:flex-row'>
          <div
            className={`py-0 font-title text-xl ${
              state.mode === PageMode.edit ? 'my-1e' : 'mb-1e'
            }`}
          >
            {set.socialMediaTags.title}
          </div>
        </div>
        <div className='w-full mb-1e text-color-pb-text'>
          {state.mode === PageMode.edit ? (
            <Form.Field
              name='details.description'
              component={
                <PageBuilder.TextEdit
                  mentions={mentions}
                  id='interactionDescription'
                  state={state}
                  isRtl={isLangRtl(lang)}
                  placeholder='Describe the webinar'
                  showInlineToolbar={false}
                  showSideToolbar={false}
                />
              }
            />
          ) : (
            <Truncate
              lines={isMobile ? 3 : 10}
              css={css`
                word-break: break-word;
              `}
              type='div'
            >
              <FilledMarkdown
                css={css`
                  p {
                    display: inline;
                  }
                  p:after {
                    content: '\\a';
                    white-space: pre;
                  }
                `}
                md={interaction.description()}
              />
            </Truncate>
          )}
        </div>
        <Form.Field
          name='details.ewebinarSession'
          className='reg-field z-20'
          component={
            <SessionsDropdown
              language={lang}
              sessions={ewebinarSession}
              isRegister={state.mode !== PageMode.public}
              primaryColor={nextWebinar.primaryColor}
              upward
            />
          }
        />
        <RegisterNowButton
          interaction={interaction}
          state={state}
          disabled={!watch('details.ewebinarSession')}
          onRegister={onRegister}
        />

        {formError && <ErrorMessage error={formError} className='mt-1e mb-1/2e' />}
      </div>

      <Portal containerId='actionsOverlay'>
        <AnimatePresence>
          {openRegForm && (
            <motion.div
              initial={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: 0.5, type: 'tween', delay: 0.25 },
              }}
              css={css`
                .RegistrationFormModal {
                  height: 100% !important;
                }
              `}
            >
              <Suspense>
                <RegistrationForm
                  ewebinar={nextWebinar}
                  set={set}
                  pixelID={set.setSettings.pixelID ?? undefined}
                  isWidget={false}
                  justInTimeDate={endOfWebinarSessionDate}
                  justInTimeName={endOfWebinarName}
                  defaultInfo={{
                    firstName: attendee?.firstName ?? undefined,
                    lastName: attendee?.lastName ?? undefined,
                    email: attendee?.email ?? undefined,
                    attendeeFields: attendee?.attendeeFields,
                    session: (watch('details.ewebinarSession') as Session | undefined)?.value,
                  }}
                  onClose={onClose}
                  onSuccess={onSubmitReaction}
                />
              </Suspense>
            </motion.div>
          )}
        </AnimatePresence>
      </Portal>
    </div>
  );
});
